import api from './api';
import { authService } from './authService';
import io from 'socket.io-client';
import offlineStorage from './offlineStorage';

const socket = io(import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001');

// Online/offline durumunu kontrol et
const isOnline = () => navigator.onLine;

export const orderService = {
  // Tüm siparişleri getir (hem aktif hem tamamlanmış)
  getOrders: async (timeRange = 'today') => {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.restaurantId) {
        throw new Error('Restaurant bilgisi bulunamadı');
      }

      // Online ise API'den çek
      if (isOnline()) {
        try {
          // Önce aktif siparişleri al
          const activeResponse = await api.get(`/api/business/orders`, {
            params: {
              status: 'pending,preparing,ready',
              timeRange: timeRange,
              restaurant: user.restaurantId
            }
          });
          
          // Sonra tamamlanmış siparişleri al
          const completedResponse = await api.get(`/api/business/orders`, {
            params: {
              status: 'delivered,cancelled',
              restaurant: user.restaurantId,
              timeRange: timeRange
            }
          });
          
          // İki veri setini birleştir
          const activeOrders = activeResponse.data.data || [];
          const completedOrders = completedResponse.data.data || [];
          const allOrders = [...activeOrders, ...completedOrders];
          
          // Cache'e kaydet
          await offlineStorage.saveActiveOrders(activeOrders);
          await offlineStorage.saveCompletedOrders(completedOrders);
          await offlineStorage.setLastUpdateTime();
          
          return {
            data: allOrders,
            success: true
          };
        } catch (error) {
          console.warn('API hatası, cache\'den okunuyor:', error);
          // API hatası durumunda cache'den oku
          const activeOrders = await offlineStorage.getActiveOrders();
          const completedOrders = await offlineStorage.getCompletedOrders();
          return {
            data: [...activeOrders, ...completedOrders],
            success: true,
            fromCache: true
          };
        }
      } else {
        // Offline durumda cache'den oku
        const activeOrders = await offlineStorage.getActiveOrders();
        const completedOrders = await offlineStorage.getCompletedOrders();
        return {
          data: [...activeOrders, ...completedOrders],
          success: true,
          fromCache: true
        };
      }
    } catch (error) {
      console.error('Siparişleri getirme hatası:', error);
      // Hata durumunda da cache'den dene
      try {
        const activeOrders = await offlineStorage.getActiveOrders();
        const completedOrders = await offlineStorage.getCompletedOrders();
        return {
          data: [...activeOrders, ...completedOrders],
          success: true,
          fromCache: true
        };
      } catch (cacheError) {
        return {
          data: [],
          success: false,
          error: error.message
        };
      }
    }
  },

  getActiveOrders: async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.restaurantId) {
        throw new Error('Restaurant bilgisi bulunamadı');
      }

      // Online ise API'den çek ve cache'le
      if (isOnline()) {
        try {
          const response = await api.get(`/api/business/orders`, {
            params: {
              status: 'pending,preparing,ready',
              timeRange: 'today',
              restaurant: user.restaurantId
            },
            timeout: 10000 // 10 saniye timeout
          });
          
          const orders = response.data.data || [];
          
          // Cache'e kaydet
          await offlineStorage.saveActiveOrders(orders);
          await offlineStorage.setLastUpdateTime();
          
          return response.data;
        } catch (error) {
          console.warn('API hatası, cache\'den okunuyor:', error);
          // API hatası durumunda cache'den oku
          const cachedOrders = await offlineStorage.getActiveOrders();
          return {
            data: cachedOrders,
            success: true,
            fromCache: true
          };
        }
      } else {
        // Offline durumda cache'den oku
        const cachedOrders = await offlineStorage.getActiveOrders();
        return {
          data: cachedOrders,
          success: true,
          fromCache: true
        };
      }
    } catch (error) {
      console.error('Siparişler getirme hatası:', error);
      // Hata durumunda da cache'den dene
      try {
        const cachedOrders = await offlineStorage.getActiveOrders();
        return {
          data: cachedOrders,
          success: true,
          fromCache: true
        };
      } catch (cacheError) {
        throw error;
      }
    }
  },

  getCompletedOrders: async (timeRange = 'today') => {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.restaurantId) {
        throw new Error('Restaurant bilgisi bulunamadı');
      }

      // Online ise API'den çek ve cache'le
      if (isOnline()) {
        try {
          const response = await api.get(`/api/business/orders`, {
            params: {
              status: 'delivered,cancelled',
              restaurant: user.restaurantId,
              timeRange: timeRange
            }
          });

          const orders = response.data.data || [];
          
          // Cache'e kaydet
          await offlineStorage.saveCompletedOrders(orders);
          await offlineStorage.setLastUpdateTime();

          return response.data;
        } catch (error) {
          console.warn('API hatası, cache\'den okunuyor:', error);
          // API hatası durumunda cache'den oku
          const cachedOrders = await offlineStorage.getCompletedOrders();
          return {
            data: cachedOrders,
            success: true,
            fromCache: true
          };
        }
      } else {
        // Offline durumda cache'den oku
        const cachedOrders = await offlineStorage.getCompletedOrders();
        return {
          data: cachedOrders,
          success: true,
          fromCache: true
        };
      }
    } catch (error) {
      // Hata durumunda da cache'den dene
      try {
        const cachedOrders = await offlineStorage.getCompletedOrders();
        return {
          data: cachedOrders,
          success: true,
          fromCache: true
        };
      } catch (cacheError) {
        throw error;
      }
    }
  },

  getOrderStats: async (timeRange = 'today') => {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.restaurantId) {
        throw new Error('Restaurant bilgisi bulunamadı');
      }

      const response = await api.get(`/api/business/orders/stats`, {
        params: {
          timeRange
        }
      });

      const stats = response.data.data || {};
      
      return {
        totalOrders: stats.totalOrders || 0,
        dailySoldItems: stats.deliveredItems || 0,
        dailyEarnings: stats.deliveredEarnings || 0,
        activeOrders: stats.activeOrders || 0,
        deliveredOrders: stats.deliveredOrders || 0,
        cancelledOrders: stats.cancelledOrders || 0,
        averageAmount: stats.averageAmount || 0,
        maxOrderAmount: stats.maxOrderAmount || 0,
        monthlyRevenue: stats.monthlyRevenue || 0,
        yearlyRevenue: stats.yearlyRevenue || 0,
        monthlyRevenues: stats.monthlyRevenues || [],
        orderSizes: stats.orderSizes || {
          small: { percentage: 30, avgAmount: 120, count: 15 },
          medium: { percentage: 45, avgAmount: 200, count: 22 },
          large: { percentage: 25, avgAmount: 300, count: 12 }
        },
        tipFeedback: stats.tipFeedback || null,
        weeklyDayTraffic: stats.weeklyDayTraffic || [],
        dayHourTraffic: stats.dayHourTraffic || [],
        topDayHour: stats.topDayHour || null
      };
    } catch (error) {
      console.error('Dashboard stats getirme hatası:', error);
      throw error;
    }
  },

  updateOrderStatus: async (orderId, status, deliveredBy = null) => {
    const statusMap = {
      'Beklemede': 'pending',
      'Hazırlanıyor': 'preparing',
      'Hazır': 'ready',
      'Teslim Edildi': 'delivered',
      'İptal Edildi': 'cancelled'
    };

    const newStatus = statusMap[status] || status;

    try {
      const body = { status: newStatus };
      if (newStatus === 'delivered' && deliveredBy) {
        body.deliveredBy = deliveredBy;
      }
      
      const response = await api.put(`/api/business/orders/${orderId}/status`, body);
      
      // Socket ile durum güncellemesini yayınla
      socket.emit('orderStatusUpdated', {
        orderId,
        status: newStatus,
        restaurantId: authService.getCurrentUser()?.restaurantId
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Dashboard istatistiklerini getir
  getDashboardStats: async (timeRange = 'today') => {
    try {
      const response = await api.get(`/api/business/orders/stats`, {
        params: { timeRange }
      });
      
      const stats = response.data.data || {};
      
      return {
        success: true,
        data: {
          dailyOrders: stats.totalOrders || 0,
          dailySoldItems: stats.deliveredItems || 0,
          dailyEarnings: stats.deliveredEarnings || 0,
          activeOrders: stats.activeOrders || 0,
          deliveredOrders: stats.deliveredOrders || 0,
          cancelledOrders: stats.cancelledOrders || 0,
          averageAmount: stats.averageAmount || 0,
          maxOrderAmount: stats.maxOrderAmount || 0,
          orderSizes: stats.orderSizes || {
            small: { percentage: 30, avgAmount: 50, count: 15 },
            medium: { percentage: 45, avgAmount: 120, count: 22 },
            large: { percentage: 25, avgAmount: 200, count: 12 }
          },
          hourlyTraffic: stats.hourlyTraffic || [],
          workingHours: stats.workingHours || null
        }
      };
    } catch (error) {
      console.error('Dashboard stats getirme hatası:', error);
      throw error;
    }
  },

  // Müşteri analizi
  getCustomerAnalytics: async () => {
    try {
      const response = await api.get('/api/business/orders/customer-analytics');
      return response.data;
    } catch (error) {
      console.error('Müşteri analizi getirme hatası:', error);
      throw error;
    }
  }
}; 